import { Button, Tooltip } from '@mui/material';
import { Pencil, Power, Trash2 } from 'lucide-react';
import React from 'react';
import type { IntegrationClient } from '../../types';
import { useTranslation } from 'react-i18next';

interface IntegrationClientHeaderProps {
  client: IntegrationClient;
  hasActiveBindings: boolean;
  busy: boolean;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

export const IntegrationClientHeader: React.FC<IntegrationClientHeaderProps> = ({
  client,
  hasActiveBindings,
  busy,
  onEdit,
  onToggleStatus,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isActive = client.status === 'active';

  return (
    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-gray-900 dark:text-slate-100">
          {client.name}
        </div>
        <div className="truncate font-mono text-xs text-gray-400 dark:text-slate-500">
          {client.public_id}
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        <Button
          type="button"
          size="small"
          variant="text"
          startIcon={<Pencil size={14} />}
          disabled={busy}
          onClick={onEdit}
        >
          {t('apiKeys.integrationClients.edit')}
        </Button>
        <Button
          type="button"
          size="small"
          variant="text"
          startIcon={<Power size={14} />}
          disabled={busy}
          onClick={onToggleStatus}
        >
          {isActive
            ? t('apiKeys.integrationClients.setInactive')
            : t('apiKeys.integrationClients.setActive')}
        </Button>
        <Tooltip
          title={hasActiveBindings ? t('apiKeys.integrationClients.deleteBlockedHint') : ''}
          arrow
        >
          <span>
            <Button
              type="button"
              size="small"
              variant="text"
              color="error"
              startIcon={<Trash2 size={14} />}
              disabled={busy || hasActiveBindings}
              onClick={onDelete}
            >
              {t('apiKeys.integrationClients.delete')}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};
